import React, { useEffect, useState } from "react";
import { Alert, Spin } from "antd";
import { SERVER_URL } from "../../../databucket";
import { useSelector } from "react-redux";
import { AntdButton } from "../../../blueplanitUtils/InputField";

const RingCentral = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [loading, setLoading] = useState(true);

  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

  // Grab token from URL/localStorage (unchanged)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    if (tokenFromUrl) {
      localStorage.setItem("rc_access_token", tokenFromUrl);
      setAccessToken(tokenFromUrl);
      setIsAuthenticated(true);
      setLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      const storedToken = localStorage.getItem("rc_access_token");
      if (storedToken) {
        setAccessToken(storedToken);
        setIsAuthenticated(true);
      }
      setLoading(false);
    }
  }, []);

  // Load Embeddable (with NR) and initialize via adapter manual-init
  useEffect(() => {
    if (!(isAuthenticated && accessToken)) return;

    // ✅ Use the adapter's manual init (NOT RC_WIDGET_MANUAL)
    window.RC_EMBEDDABLE_ADAPTER_MANUAL_INIT = true;

    if (!document.getElementById("rc-adapter-script")) {
      const script = document.createElement("script");
      script.id = "rc-adapter-script";

      // ✅ Must load from apps.ringcentral.com; NR flag required on 1.10.x (safe on 2.x)
      script.src =
        "https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?enableNoiseReductionSetting=1";

      script.onload = () => {
        // Initialize adapter
        if (window.RCAdapterInit) {
          window.RCAdapterInit();
        }

        // Set auth
        if (window.rcAdapter) {
          window.rcAdapter.setAuthData({
            tokenType: "Bearer",
            accessToken,
            expiresIn: 3600,
          });
        }

        // Optional: minimize the widget after it appears
        setTimeout(() => {
          const iframe = document.querySelector("#rc-widget-adapter-frame");
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: "rc-adapter-minimize" }, "*");
          }
        }, 1000);
      };

      document.body.appendChild(script);
    }
  }, [isAuthenticated, accessToken]);

  const handleSignIn = () => {
    if (loginUser?.id) {
      setAuthError(false);
      const url = `${SERVER_URL}/api/oauth/authorize?userId=${loginUser.id}&returnUrl=${encodeURIComponent(window.location.origin)}`;
      window.open(url, "_blank");
    } else {
      setAuthError(true);
    }
  };

  useEffect(() => {
    if (
      !isAuthenticated &&
      !accessToken &&
      !window.location.search.includes("token") &&
      loginUser?.id &&
      !loading
    ) {
      handleSignIn();
    }
    if (!isAuthenticated && !loginUser?.id && !loading) {
      setAuthError(true);
    }
  }, [isAuthenticated, loginUser, accessToken, loading]);

  if (loading) {
    return (
      <div style={{ marginTop: 100, display: "flex", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div
        style={{
          marginTop: 100,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {authError ? (
          <>
            <Alert
              message="Authentication was unsuccessful. Please try again."
              type="error"
              showIcon
              style={{ marginBottom: 24, width: 400, maxWidth: "90%" }}
            />
            <AntdButton type="primary" onClick={handleSignIn} label={"Sign in with RingCentral"} />
          </>
        ) : (
          <Spin tip="Authenticating with RingCentral..." size="large" />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 100,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Alert
        message="RingCentral authentication successful!"
        type="success"
        showIcon
        style={{ marginBottom: 24, width: 400, maxWidth: "90%" }}
      />
      {/* Keep the widget visible to users (minimized is fine).
          Don't hide it with display:none or the settings page (with NR) may not render. */}
      <div id="rc-widget" />
    </div>
  );
};

export default RingCentral;
